import { NgModule } from '@angular/core';
import {
    RouterModule,
    Routes,
} from '@angular/router';
import { UnsavedFormGuard } from '@guards';
import {
    AlertsPageComponent,
    AssetsPageComponent,
    AuditPageComponent,
    EnvironmentsPageComponent,
    JobsPageComponent,
    LicensePageComponent,
    LogsPageComponent,
    PackagesPageComponent,
    ProcessesPageComponent,
    ProfilePageComponent,
    QueuesPageComponent,
    QueueUpsertPageComponent,
    ReviewTransactionsPageComponent,
    RobotsPageComponent,
    RolesPageComponent,
    SettingsPageComponent,
    StatsPageComponent,
    TransactionsPageComponent,
    TriggersPageComponent,
    UsersPageComponent,
} from '@pages';
import { AlertModule } from '@pages/alerts/alert.module';
import { AssetModule } from '@pages/assets/asset.module';
import { AuditModule } from '@pages/audit/audit.module';
import { CredentialStoresModule } from '@pages/credential-stores/credential-stores.module';
import { CredentialStoresComponent } from '@pages/credential-stores/credential-stores.page';
import { EnvironmentModule } from '@pages/environments/environment.module';
import { FoldersModule } from '@pages/folders/folders.module';
import { FoldersPageComponent } from '@pages/folders/folders.page';
import { JobModule } from '@pages/jobs/job.module';
import { LicenseModule } from '@pages/license/license.module';
import { LogModule } from '@pages/logs/log.module';
import { MachineModule } from '@pages/machines/machine.module';
import { MachinesPageComponent } from '@pages/machines/machines.page';
import { LibrariesPageComponent } from '@pages/packages/libraries.page';
import { NugetModule } from '@pages/packages/nuget.module';
import { ProcessModule } from '@pages/process/process.module';
import { ProfileModule } from '@pages/profile/profile.module';
import { QueueModule } from '@pages/queues/queue.module';
import { RobotModule } from '@pages/robots/robot.module';
import { RoleModule } from '@pages/roles/role.module';
import { SettingsModule } from '@pages/settings/settings.module';
import { RestrictedAccessPageComponent } from '@pages/shared/restricted-access';
import { SharedPageModule } from '@pages/shared/shared-page.module';
import { StatsModule } from '@pages/stats/stats.module';
import { TransactionModule } from '@pages/transactions/transaction.module';
import { TriggerModule } from '@pages/triggers/triggers.module';
import { TriggerUpsertPageComponent } from '@pages/triggers/upsert/trigger.upsert.page';
import { UserModule } from '@pages/users/user.module';

export const TENANT_PAGE_MODULES = [
    StatsModule,
    SharedPageModule,
    RobotModule,
    MachineModule,
    LogModule,
    EnvironmentModule,
    NugetModule,
    ProcessModule,
    AssetModule,
    QueueModule,
    TransactionModule,
    AuditModule,
    JobModule,
    LicenseModule,
    RoleModule,
    UserModule,
    SettingsModule,
    TriggerModule,
    AlertModule,
    FoldersModule,
    ProfileModule,
    CredentialStoresModule,
];

export const TENANT_ROUTES: Routes = [
    {
        path: 'user-activity',
        component: RobotsPageComponent,
        data: {
            isModernContext: true,
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    },
    {
        path: 'robots',
        component: RobotsPageComponent,
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    },
    {
        path: ':entity/:id/logs',
        component: LogsPageComponent,
    },  {
        path: 'logs',
        component: LogsPageComponent,
    }, {
        path: 'environments',
        component: EnvironmentsPageComponent,
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    },
    {
        path: 'machines',
        component: MachinesPageComponent,
    }, {
        path: 'processes',
        component: ProcessesPageComponent,
    },
    {
        path: 'packages',
        component: PackagesPageComponent,
    },
    {
        path: 'libraries',
        component: LibrariesPageComponent,
    },
    {
        path: 'assets',
        component: AssetsPageComponent,
    },
    {
        path: 'queues',
        children: [
            {
                path: '',
                component: QueuesPageComponent,
            },
            {
                path: 'add',
                component: QueueUpsertPageComponent,
                canDeactivate: [UnsavedFormGuard],
            },
            {
                path: ':id',
                children: [
                    {
                        path: '',
                        redirectTo: 'edit',
                        pathMatch: 'full',
                    },
                    {
                        path: 'edit',
                        component: QueueUpsertPageComponent,
                        canDeactivate: [UnsavedFormGuard],
                    },
                    {
                        path: 'transactions',
                        component: TransactionsPageComponent,
                    },
                ],
            },
        ],
    },
    {
        path: 'audit',
        component: AuditPageComponent,
    },
    {
        path: 'jobs',
        component: JobsPageComponent,
    },
    {
        path: 'roles',
        component: RolesPageComponent,
    },
    {
        path: 'users',
        component: UsersPageComponent,
    },
    {
        // alias for front page
        path: 'dashboard',
        redirectTo: '/',
    },
    {
        path: 'settings',
        component: SettingsPageComponent,
        canDeactivate: [UnsavedFormGuard],
    },
    {
        path: 'schedules',
        redirectTo: '/triggers',
    },
    {
        path: 'triggers',
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        children: [
            {
                path: '',
                component: TriggersPageComponent,
            },
            {
                path: 'add',
                component: TriggerUpsertPageComponent,
                canDeactivate: [UnsavedFormGuard],
            },
            {
                path: ':id',
                children: [
                    {
                        path: 'edit',
                        component: TriggerUpsertPageComponent,
                        canDeactivate: [UnsavedFormGuard],
                    },
                    {
                        path: 'jobs',
                        component: JobsPageComponent,
                    },
                ],
            },
        ],
    },
    {
        path: 'alerts',
        component: AlertsPageComponent,
    }, {
        path: 'restricted-access',
        component: RestrictedAccessPageComponent,
    },
    {
        path: 'folders',
        component: FoldersPageComponent,
    },
    {
        path: 'folders/:id',
        component: FoldersPageComponent,
    },
    {
        path: 'profile',
        component: ProfilePageComponent,
    },
    {
        path: 'profile/:id',
        component: ProfilePageComponent,
    },
    {
        path: 'review-requested',
        component: ReviewTransactionsPageComponent,
    },
    {
        path: 'review-requested/:id',
        component: ReviewTransactionsPageComponent,
    },
    {
        path: 'license',
        component: LicensePageComponent,
    },
    {
        path: 'license/:type',
        component: LicensePageComponent,
    },
    {
        path: 'credential-stores',
        component: CredentialStoresComponent,
    },
    {
        path: '',
        component: StatsPageComponent,
    },
    {
        path: 'webhooks',
        loadChildren: './pages/webhooks/webhook.module#WebhookModule',
    },
    {
        path: 'tasks',
        loadChildren: './pages/tasks/task.module#TaskModule',
    },
    {
        path: 'monitoring',
        loadChildren: './pages/monitoring/monitoring.module#MonitoringModule',
    },
    {
        path: 'insights',
        loadChildren: './pages/analytics/analytics.module#AnalyticsModule',
    },
    {
        path: 'ml',
        loadChildren: './pages/ml/ml.module#MLModule',
    },
    {
        path: '*',
        component: StatsPageComponent,
    },
    {
        path: '**',
        redirectTo: '/restricted-access',
    },
];

@NgModule({
    imports: [
        ...TENANT_PAGE_MODULES,
        RouterModule.forChild(TENANT_ROUTES),
    ],
    exports: [RouterModule],
})
export class TenantRouteModule { }
